import { useState, useEffect } from "react";
import "./AutosPage.css";

const CATEGORIES = ["All", "Economic", "Sedan", "4x4", "Van"];

const CATEGORY_ICONS = {
  Economic: "⚡",
  Sedan: "🚗",
  "4x4": "🏔️",
  Van: "🚐"
};

const emptyBooking = { name: "", email: "", phone: "", pickupDate: "", returnDate: "" };

export default function AutosPage() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedCar, setSelectedCar] = useState(null);
  const [booking, setBooking] = useState(emptyBooking);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch("/api/rentals/cars")
      .then(r => r.json())
      .then(data => { setCars(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedCar) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [selectedCar]);

  const filtered = activeCategory === "All" ? cars : cars.filter(c => c.category === activeCategory);

  function openBooking(car) {
    setSelectedCar(car);
    setBooking(emptyBooking);
    setSuccess(false);
    setErr("");
  }

  function closeBooking() {
    setSelectedCar(null);
    setSuccess(false);
    setErr("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      const res = await fetch("/api/rentals/bookings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...booking, carId: selectedCar._id })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Booking failed");
      setSuccess(true);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="autos-page">

      {/* PAGE HEADER */}
      <div className="autos-header">
        <div className="autos-header-content container">
          <div className="eyebrow"><div className="eline"></div><span className="etxt">Car Rental</span></div>
          <h1>Explore Albania <span>Your Way</span></h1>
          <p>Hand-picked vehicles for every journey — from coastal roads to mountain passes.</p>
        </div>
      </div>

      {/* FILTERS */}
      <div className="autos-filters-wrap">
        <div className="autos-filters container">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={"autos-filter-btn" + (activeCategory === cat ? " active" : "")}
              onClick={() => setActiveCategory(cat)}
            >
              {cat !== "All" && <span>{CATEGORY_ICONS[cat]}</span>}
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* CARS GRID */}
      <div className="container autos-container">
        {loading && (
          <div className="autos-empty">
            <div className="autos-empty-icon">🚗</div>
            <p>Loading vehicles...</p>
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="autos-empty">
            <div className="autos-empty-icon">{activeCategory !== "All" ? CATEGORY_ICONS[activeCategory] : "🚗"}</div>
            <h3>No vehicles available</h3>
            <p>Check back soon — we're adding more cars to our fleet.</p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="autos-grid">
            {filtered.map(car => (
              <div key={car._id} className="car-card">
                <div className="car-card-img-wrap">
                  {car.photo
                    ? <img src={car.photo} alt={car.name} className="car-card-img" />
                    : <div className="car-card-img-placeholder"><span>🚗</span></div>
                  }
                  <div className="car-card-category">{CATEGORY_ICONS[car.category]} {car.category}</div>
                </div>
                <div className="car-card-body">
                  <h3 className="car-card-name">{car.name}</h3>
                  <div className="car-card-icons">
                    <div className="car-icon-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      <span>{car.seats} seats</span>
                    </div>
                    <div className="car-icon-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                      <span>{car.transmission}</span>
                    </div>
                  </div>
                  {car.description && <p className="car-card-desc">{car.description}</p>}
                  <div className="car-card-footer">
                    <div className="car-card-price">
                      <span className="car-price-amount">€{car.pricePerDay}</span>
                      <span className="car-price-unit">/day</span>
                    </div>
                    <button className="car-book-btn" onClick={() => openBooking(car)}>Book Now</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* BOOKING MODAL */}
      {selectedCar && (
        <div className="car-modal-overlay" onClick={closeBooking}>
          <div className="car-modal" onClick={e => e.stopPropagation()}>
            <button className="car-modal-close" onClick={closeBooking}>×</button>

            {success ? (
              <div className="car-modal-success">
                <div className="car-modal-success-icon">✓</div>
                <h2>Booking Received!</h2>
                <p>Thank you <strong>{booking.name}</strong>. We'll contact you at <strong>{booking.phone}</strong> to confirm your reservation for the <strong>{selectedCar.name}</strong>.</p>
                <button className="car-book-btn" style={{ marginTop: 24 }} onClick={closeBooking}>Close</button>
              </div>
            ) : (
              <>
                <div className="car-modal-header">
                  {selectedCar.photo && (
                    <div className="car-modal-img-wrap">
                      <img src={selectedCar.photo} alt={selectedCar.name} className="car-modal-img" />
                    </div>
                  )}
                  <div className="car-modal-car-info">
                    <div className="car-modal-cat">{CATEGORY_ICONS[selectedCar.category]} {selectedCar.category}</div>
                    <h2 className="car-modal-name">{selectedCar.name}</h2>
                    <div className="car-modal-meta">
                      <span className="car-icon-item">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                        {selectedCar.seats} seats
                      </span>
                      <span className="car-icon-item">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                        {selectedCar.transmission}
                      </span>
                      <span className="car-modal-price">€{selectedCar.pricePerDay}/day</span>
                    </div>
                  </div>
                </div>

                <div className="car-modal-body">
                  <h3 className="car-modal-form-title">Reserve This Vehicle</h3>
                  {err && <div className="car-modal-err">{err}</div>}
                  <form onSubmit={handleSubmit} className="car-modal-form">
                    <div className="car-form-row">
                      <div className="car-form-field">
                        <label>Full Name</label>
                        <input
                          type="text"
                          placeholder="John Smith"
                          value={booking.name}
                          onChange={e => setBooking(b => ({ ...b, name: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="car-form-field">
                        <label>Phone Number</label>
                        <input
                          type="tel"
                          placeholder="+355 69 000 0000"
                          value={booking.phone}
                          onChange={e => setBooking(b => ({ ...b, phone: e.target.value }))}
                          required
                        />
                      </div>
                    </div>
                    <div className="car-form-field">
                      <label>Email Address</label>
                      <input
                        type="email"
                        placeholder="john@example.com"
                        value={booking.email}
                        onChange={e => setBooking(b => ({ ...b, email: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="car-form-row">
                      <div className="car-form-field">
                        <label>Pick-up Date</label>
                        <input
                          type="date"
                          value={booking.pickupDate}
                          min={new Date().toISOString().split("T")[0]}
                          onChange={e => setBooking(b => ({ ...b, pickupDate: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="car-form-field">
                        <label>Return Date</label>
                        <input
                          type="date"
                          value={booking.returnDate}
                          min={booking.pickupDate || new Date().toISOString().split("T")[0]}
                          onChange={e => setBooking(b => ({ ...b, returnDate: e.target.value }))}
                          required
                        />
                      </div>
                    </div>
                    <button type="submit" className="car-book-btn car-book-btn-full" disabled={submitting}>
                      {submitting ? "Sending..." : "Confirm Booking Request"}
                    </button>
                    <p className="car-modal-note">We'll contact you within 24 hours to confirm availability.</p>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}